import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
import { FilterValues } from 'src/app/shared/components/filter/filter.component';
import { EstampApiService } from 'src/app/core/services/estamp-api.service';
import { EstampApiKeyService } from 'src/app/core/services/estamp-api-key.service';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrl: './inventory.component.scss'
})
export class InventoryComponent implements OnInit, OnDestroy {

  loading = false;
  inventory: any[] = [];
  orderDetail: any = {};

  showModal: boolean = false;
  @ViewChild('dt') table!: Table;

  private subscriptions = new Subscription();

  constructor(
    private estampApi: EstampApiService,
    private apiKeyService: EstampApiKeyService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    // Reload the list as soon as a demo API key becomes available.
    this.subscriptions.add(
      this.apiKeyService.changes.subscribe(() => {
        if (this.apiKeyService.hasApiKey && this.table) {
          this.table.reset();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getInventory(event: LazyLoadEvent) {
    if (!this.apiKeyService.hasApiKey) {
      this.inventory = [];
      this.totalRecords = 0;
      return;
    }

    Promise.resolve().then(() => this.loading = true);
    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.rows;
    const page = first / rows + 1;

    this.estampApi.getScannedStamps(page, rows).subscribe(
      (res: any) => {
        const stamps = this.extractStamps(res);

        this.inventory = this.applyFilters(stamps);
        this.totalRecords = this.extractTotalCount(res, stamps.length);
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  closeModal() {
    this.showModal = false;
  }

  previewFile(document: any) {
    if (document?.pdfUrl) {
      window.open(document.pdfUrl, '_blank');
    }
  }


  totalRecords = 0;
  rows = 10;
  searchText: any = '';
  selectedStatus: any = '';
  rangeDates: Date[] | null = null;

  statusOptions = [
    { label: 'All', value: '' },
    { label: 'Utilized', value: 'UTILIZED' },
    { label: 'Allocated', value: 'ALLOCATED' }
  ];


  onFilterChange(values: FilterValues) {
    this.searchText = values.search;
    this.selectedStatus = values.status;
    this.rangeDates = values.dateRange;
    this.table.reset();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.searchText ||
      this.selectedStatus ||
      (this.rangeDates && this.rangeDates.length === 2)
    );
  }

  navigateStampOrderCreate = () => {
    this.router.navigate(['/customer/estamp/create-customer-order']);
  }


  private extractStamps(res: any): any[] {
    const stamps =
      res?.data?.scannedStamps ??
      res?.data ??
      res?.scannedStamps ??
      res?.items ??
      res;

    return Array.isArray(stamps) ? stamps : [];
  }

  private extractTotalCount(res: any, fallback: number): number {
    return (
      res?.meta?.total ??
      res?.totalCount ??
      res?.total ??
      res?.pagination?.total ??
      fallback
    );
  }

  /**
   * The demo API paginates with `page` & `limit` only, so search / status /
   * date filters are applied to the rows of the current page.
   */
  private applyFilters(stamps: any[]): any[] {
    if (!this.hasActiveFilters) {
      return stamps;
    }

    const search = String(this.searchText ?? '').trim().toLowerCase();

    return stamps.filter(stamp => {
      if (search) {
        const haystack = [
          stamp?.batchId,
          stamp?.serialNumber,
          stamp?.stampValue,
          stamp?.stateName
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (this.selectedStatus && stamp?.status !== this.selectedStatus) {
        return false;
      }

      if (this.rangeDates && this.rangeDates.length === 2 && stamp?.createdAt) {
        const createdAt = new Date(stamp.createdAt).getTime();
        const from = this.startOfDay(this.rangeDates[0]).getTime();
        const to = this.endOfDay(this.rangeDates[1]).getTime();

        if (isNaN(createdAt) || createdAt < from || createdAt > to) {
          return false;
        }
      }

      return true;
    });
  }

  private startOfDay(date: Date): Date {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  private endOfDay(date: Date): Date {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  }


  getStatusClass(status: string): any {
    switch (status) {
      case 'ORDER_CREATED':
        return 'border-secondary text-secondary bg-light';

      case 'UNDER_REVIEW':
      case 'UNDER_CHECKER_REVIEW':
        return 'border-warning text-warning bg-warning-subtle';

      case 'INVOICE_GENERATED':
        return 'border-info text-info bg-info-subtle';

      case 'PAYMENT_SUBMITTED':
        return 'border-primary text-primary bg-primary-subtle';

      case 'PAYMENT_REJECTED':
        return 'border-danger text-danger bg-danger-subtle';

      case 'PAYMENT_VERIFIED':
        return 'border-success text-success bg-success-subtle';

      case 'ESTAMP_UPLOADED':
        return 'border-purple text-purple bg-purple-subtle';

      case 'COMPLETED':
        return 'border-success text-success bg-success-subtle';

      default:
        return 'border-dark text-dark bg-light';
    }
  }
}
